import { Box, Typography } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useRef, useState } from 'react';
import type { FC } from 'react';
import rfidGroupsComparator from 'utils/helpers/rfidGroupsComparator';

import Button from 'components/Button';
import FilledTextField from 'components/MuiCustom/FilledTextField';

import { useAppDispatch, useAppSelector } from 'redux-store';
import addRfidGroupThunk from 'redux-store/thunks/rfidGroups/addRfidGroupThunk';
import getAllRfidGroupsThunk from 'redux-store/thunks/rfidGroups/getAllRfidGroupsThunk';

import type { AddRfidsToGroupsModalStageProps } from '..';

const AddNewRfidGroup: FC<AddRfidsToGroupsModalStageProps> = ({
  state,
  setState,
  onCloseProxy,
}) => {
  const { name } = state;

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const groupNameInputRef = useRef<HTMLInputElement>(null);

  const handleChangeName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setState(prev => ({ ...prev, name: e.target.value }));
    },
    [setState],
  );

  const rfidGroups = useAppSelector(
    appState => appState.rfidGroups.items,
    (a, b) => rfidGroupsComparator()(a, b),
  );

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleCreateGroup = useCallback(async () => {
    let result = undefined;
    if (!name) {
      setError(
        t('common:pages.rfid_groups.add_rfid_group.errors.name_required'),
      );

      return;
    }

    if (name.length > 50) {
      setError(
        t('common:pages.rfid_groups.add_rfid_group.group_name_max_length'),
      );

      return;
    }

    if (rfidGroups.map(g => g.name).includes(name)) {
      setError(
        t('common:pages.rfid_groups.add_rfid_group.errors.name_already_exists'),
      );

      return;
    }

    try {
      setLoading(true);

      result = await dispatch(
        addRfidGroupThunk({
          rfidGroup: {
            name: state.name,
          },
        }),
      ).unwrap();

      if (!result.success) {
        setError(
          t(
            'common:pages.rfid_groups.add_rfid_group.errors.error_creating_group_with_error',
            { error: result.error },
          ),
        );
        setLoading(false);

        return;
      }

      setError(null);

      setState({
        name: '',
        rfids: [],
        stage: 'name',
        newGroupID: result.rfidGroup.id,
      });

      await dispatch(getAllRfidGroupsThunk({})).unwrap();

      setTimeout(() => {
        setState({
          name: '',
          rfids: [],
          stage: 'name',
        });
      }, 1250);
    } catch (e) {
      Sentry.captureException(e, {
        extra: {
          state,
          result,
        },
      });
    } finally {
      setLoading(false);
    }
  }, [rfidGroups, dispatch, name, setState, state, t]);

  return (
    <>
      <Box mb={4}>
        <Typography variant="h2" mb={2} textAlign="center">
          {t('common:pages.rfid_groups.add_rfid_group.title')}
        </Typography>
        <Typography variant="body1" textAlign="center">
          {t('common:pages.rfid_groups.add_rfid_group.new_group_desc')}
        </Typography>
      </Box>
      <Box mb={4} width="100%" minHeight={150}>
        <form
          onSubmit={e => {
            e.preventDefault();
            handleCreateGroup();
          }}
          data-testid="create-group-form"
        >
          <FilledTextField
            value={name}
            onChange={handleChangeName}
            fullWidth
            error={Boolean(error)}
            helperText={error}
            inputRef={groupNameInputRef}
            label={t('common:pages.rfid_groups.add_rfid_group.group_name')}
            data-testid="add-new-rfid-group-input"
          />
        </form>
      </Box>
      <Box
        width="100%"
        display="flex"
        flexDirection="row"
        mt={2}
        mb={2}
        gap={2}
      >
        <Button
          variant="flat"
          color="neutral"
          fullWidth
          size="large"
          disabled={loading}
          onClick={onCloseProxy}
          data-testid="modal-cancel-button"
        >
          {t('common:cancel')}
        </Button>
        <Button
          variant="flat"
          fullWidth
          size="large"
          disabled={loading || name.length === 0}
          onClick={handleCreateGroup}
          data-testid="modal-save-button"
        >
          {t('common:save')}
        </Button>
      </Box>
    </>
  );
};

export default AddNewRfidGroup;
